import React, { Fragment, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
    Spacer,
    Text,
    Flex,
} from '@zillow/constellation';

import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';
import FraudFailureModal from 'app/shared/components/Modal/FraudFailureModal';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';

import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';

import {
    StyledContainer,
    StyledBodyContainer,
    StyledGuaranteeIcon,
} from './StyledComponents';

import './style.scss';

const CreditCardCapture = forwardRef(({
    brand,
    price,
    apiAccessToken,
    isIframeLoaded,
    isCapturing,
    isCaptured,
    isEligibleForLeadGuarantee,
    onLoadIframe,
    onCleanUpIframe,
    formErrorMessage,
    fraudErrorMessage,
    displayedInSelectPaymentMethodModal,
}, ref) => {
    useEffect(() => {
        if (apiAccessToken) {
            onLoadIframe(apiAccessToken);
            return onCleanUpIframe;
        }
        return () => {};
    }, [apiAccessToken, onCleanUpIframe, onLoadIframe]);

    const showSpinner = !isIframeLoaded || isCapturing || isCaptured;

    if (fraudErrorMessage) {
        return <FraudFailureModal brand={brand} />;
    }

    return (
        <StyledContainer
            ref={ref}
            className={cx('CreditCardCapture', {
                'CreditCardCapture-defaultPaymentExists': displayedInSelectPaymentMethodModal,
            })}
        >
            <StyledBodyContainer>
                {showSpinner && <SpinnerContainer size="xl" />}
                {!showSpinner && !displayedInSelectPaymentMethodModal && (
                    <Fragment>
                        <Spacer marginBottom="md">
                            <Text fontType="body" data-test="renew-info">
                                Your listing will renew automatically until you deactivate it. Your card will be charged
                                <b>{` ${price} per week `}</b>
                                while your listing is active on the Zillow Rental Network.
                            </Text>
                        </Spacer>

                        {isEligibleForLeadGuarantee && (
                            <Spacer marginBottom="md">
                                <Flex
                                    display="flex"
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <StyledGuaranteeIcon alt="" src={zGuaranteeIcon} />
                                    <Text fontType="body">Includes Lead Guarantee</Text>
                                </Flex>
                            </Spacer>
                        )}
                    </Fragment>
                )}
                {!showSpinner && displayedInSelectPaymentMethodModal && (
                    <Spacer marginY="md">
                        <Text as="div" fontType="body" className="Text-notification">
                            This card will become the default payment method for all upcoming renewals & future listing
                            activations.
                        </Text>
                    </Spacer>
                )}
                <div
                    id={PaidInclusionConstants.CREDIT_CARD_FORM.ID_SELECTOR}
                    className={cx(
                        PaidInclusionConstants.CREDIT_CARD_FORM.CLASS_SELECTOR,
                        'fs-block',
                        {
                            hideForm: showSpinner,
                        }
                    )}
                />
                {!showSpinner && formErrorMessage && (
                    <Text fontType="body" className="Text-fail" data-test="error-text">
                        {formErrorMessage}
                    </Text>
                )}
                {!showSpinner && <ListingActivationTerms lowlight={true} actionText="By clicking Publish listing" />}
            </StyledBodyContainer>
        </StyledContainer>
    );
});

CreditCardCapture.propTypes = {
    brand: PropTypes.string.isRequired,
    isIframeLoaded: PropTypes.bool.isRequired,
    isCapturing: PropTypes.bool.isRequired,
    isCaptured: PropTypes.bool.isRequired,
    isEligibleForLeadGuarantee: PropTypes.bool,
    onLoadIframe: PropTypes.func.isRequired,
    onCleanUpIframe: PropTypes.func.isRequired,
    formErrorMessage: PropTypes.string.isRequired,
    fraudErrorMessage: PropTypes.string.isRequired,
    apiAccessToken: PropTypes.string.isRequired,
    displayedInSelectPaymentMethodModal: PropTypes.bool.isRequired,
};

CreditCardCapture.defaultProps = {
    isEligibleForLeadGuarantee: false,
};

export default CreditCardCapture;
